/**
 * Entra pickers — typeahead components over the v0.6.0 Function endpoints
 *   GET /v3/admin/entra/groups            (EntraGroupPicker)
 *   GET /v3/admin/users/entra-candidates  (EntraUserPicker)
 *
 * Both built FRESH for Domain 1.6b. Not ported from the ROP-teams sync
 * panel — endpoint contracts re-derived from Graph docs + verified against
 * the admin-panel api-client TS types.
 *
 * Common UX:
 *   - 250ms debounce on input → server-side prefix match.
 *   - Dropdown of candidate rows; click to select; clears the search box.
 *   - Disabled state hides the input + renders the persisted value (so the
 *     Edit modal can "lock in" a value while focus is elsewhere).
 *   - GRAPH_PERMISSION_DENIED returns surface a clear message pointing at
 *     the Ray-ask, since the Function MI's Graph perms are the most common
 *     failure mode for these endpoints.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import type {
  EntraGroupCandidate,
  EntraUserCandidate,
} from '@retail-ops/api-client';

interface BasePickerProps {
  disabled?: boolean;
  placeholder?: string;
  /** Optional initial query (rarely useful; defaults to empty). */
  initialQuery?: string;
}

// ─── Group picker ─────────────────────────────────────────────────────────

export interface EntraGroupPickerProps extends BasePickerProps {
  /** Currently-selected group (renders as a chip when set). Null = empty. */
  value: { id: string; displayName: string } | null;
  onChange: (group: { id: string; displayName: string } | null) => void;
}

export function EntraGroupPicker(props: EntraGroupPickerProps): JSX.Element {
  const client = useApiClient();
  return (
    <PickerCore<EntraGroupCandidate>
      disabled={props.disabled}
      placeholder={props.placeholder ?? 'Search Entra groups…'}
      initialQuery={props.initialQuery}
      value={props.value}
      onClear={() => props.onChange(null)}
      onSelect={(c) => props.onChange({ id: c.id, displayName: c.displayName })}
      fetcher={async (q) => {
        const resp = await client.retail.admin.searchEntraGroups(q, 10);
        if (!resp.success) throw new Error(resp.code ?? resp.error ?? 'Graph error');
        return resp.data;
      }}
      renderRow={(c) => (
        <div className="picker-row">
          <div className="picker-row__primary">{c.displayName}</div>
          <div className="picker-row__secondary">
            {c.mail ?? '(no mail)'} ·{' '}
            {c.mailEnabled && c.securityEnabled
              ? 'Mail-enabled security'
              : c.mailEnabled
                ? 'Distribution'
                : c.securityEnabled
                  ? 'Security'
                  : c.groupTypes.includes('Unified')
                    ? 'Microsoft 365'
                    : 'Group'}
          </div>
        </div>
      )}
      rowKey={(c) => c.id}
    />
  );
}

// ─── User picker ──────────────────────────────────────────────────────────

export interface EntraUserPickerProps extends BasePickerProps {
  onSelect: (user: EntraUserCandidate) => void;
  /** Cleared on every select (Add-user flow). No persistent value. */
}

export function EntraUserPicker(props: EntraUserPickerProps): JSX.Element {
  const client = useApiClient();
  return (
    <PickerCore<EntraUserCandidate>
      disabled={props.disabled}
      placeholder={props.placeholder ?? 'Search Entra users…'}
      initialQuery={props.initialQuery}
      value={null}
      onClear={() => {}}
      onSelect={props.onSelect}
      fetcher={async (q) => {
        const resp = await client.retail.admin.searchEntraCandidates(q, 20);
        if (!resp.success) throw new Error(resp.code ?? resp.error ?? 'Graph error');
        return resp.data;
      }}
      renderRow={(c) => (
        <div className="picker-row">
          <div className="picker-row__primary">
            {c.displayName} <span className="picker-row__upn">&lt;{c.userPrincipalName}&gt;</span>
          </div>
          <div className="picker-row__secondary">
            {c.jobTitle ?? '(no title)'} · {c.officeLocation ?? '(no office)'}
          </div>
        </div>
      )}
      rowKey={(c) => c.id}
    />
  );
}

// ─── Shared core ──────────────────────────────────────────────────────────

interface PickerCoreProps<T> {
  disabled?: boolean;
  placeholder: string;
  initialQuery?: string;
  value: { id: string; displayName: string } | null;
  onClear: () => void;
  onSelect: (row: T) => void;
  fetcher: (query: string) => Promise<ReadonlyArray<T>>;
  renderRow: (row: T) => JSX.Element;
  rowKey: (row: T) => string;
}

function PickerCore<T>(props: PickerCoreProps<T>): JSX.Element {
  const [query, setQuery] = useState(props.initialQuery ?? '');
  const [rows, setRows] = useState<ReadonlyArray<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const debounceId = useRef<number | null>(null);

  const fire = useCallback(
    async (q: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await props.fetcher(q);
        setRows(data);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        // Friendly hint for the most common deployment-time failure.
        setError(
          msg === 'GRAPH_PERMISSION_DENIED'
            ? 'Graph permission missing — see docs/ray-asks/2026-05-15-function-mi-graph-group-perms.html.'
            : msg,
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [props],
  );

  // Debounced re-fetch on query change.
  useEffect(() => {
    if (debounceId.current !== null) {
      window.clearTimeout(debounceId.current);
    }
    debounceId.current = window.setTimeout(() => {
      void fire(query);
    }, 250);
    return () => {
      if (debounceId.current !== null) window.clearTimeout(debounceId.current);
    };
  }, [query, fire]);

  // Locked / disabled state — render the persisted value as a chip.
  if (props.disabled) {
    return (
      <div className="picker picker--disabled">
        {props.value ? (
          <span className="picker-chip">{props.value.displayName}</span>
        ) : (
          <span className="picker-chip picker-chip--empty">(none)</span>
        )}
      </div>
    );
  }

  return (
    <div className="picker">
      {props.value && (
        <div className="picker__selected">
          <span className="picker-chip">{props.value.displayName}</span>
          <button
            type="button"
            className="picker__clear"
            onClick={() => {
              props.onClear();
              setQuery('');
            }}
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>
      )}
      <input
        type="text"
        className="form-input"
        placeholder={props.placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Defer so click on a row registers before we close.
          window.setTimeout(() => setOpen(false), 150);
        }}
      />
      {open && (
        <div className="picker__dropdown">
          {loading && <div className="picker__hint">Searching…</div>}
          {error && <div className="picker__error">{error}</div>}
          {!loading && !error && rows !== null && rows.length === 0 && (
            <div className="picker__hint">No matches.</div>
          )}
          {!loading && !error && rows !== null && rows.length > 0 && (
            <ul className="picker__list">
              {rows.map((r) => (
                <li
                  key={props.rowKey(r)}
                  className="picker__option"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    props.onSelect(r);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  {props.renderRow(r)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
